import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Empty } from "@/components/common";
import { usePortal } from "@/components/PortalLayout";
import { useApi, act } from "@/lib/useApi";
import { api } from "@/lib/api";
import { dt } from "@/lib/fmt";

export default function PortalCheckin() {
  const { sid } = usePortal();
  const [params, setParams] = useSearchParams();
  const [data, reload] = useApi(`/portal/students/${sid}/classes-today`, [sid]);
  const [hist, reloadHist] = useApi(`/portal/students/${sid}/checkins`, [sid]);
  const [codes, setCodes] = useState({});
  const [done, setDone] = useState(null);
  const auto = useRef(false);

  const go = async (classId, code) => {
    const r = await act(() => api.post("/checkin/self", { student_id: sid, class_id: classId, code: code || null }));
    if (r) { setDone(r); reload(); reloadHist(); }
  };

  useEffect(() => {
    const c = params.get("class"), code = params.get("code");
    if (c && code && sid && !auto.current) { auto.current = true; go(c, code); setParams({}, { replace: true }); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sid]);

  return (
    <div className="space-y-4">
      <h1 className="text-3xl font-extrabold uppercase">Check-in</h1>
      {done && <div className="rounded-2xl bg-emerald-600 text-white p-5 flex items-center gap-3 fade-up" data-testid="checkin-success">
        <CheckCircle2 className="w-10 h-10" /><div><div className="font-display text-2xl font-bold uppercase">Presença confirmada</div><div className="text-sm">{done.class_name} · {done.time}</div></div></div>}
      <div className="text-sm text-slate-600">Aulas de hoje ({data && dt(data.date)}){data?.require_qr && " · a academia exige o código do QR Code"}</div>
      {!data ? <Empty text="Carregando…" /> : !data.classes.length ? <Empty text="Nenhuma aula hoje." /> : data.classes.map((c) => (
        <div key={c.id} className="panel p-4 space-y-3" data-testid={`portal-class-${c.id}`}>
          <div className="flex items-center justify-between">
            <div><div className="font-display text-xl font-bold uppercase">{c.name}</div><div className="text-xs text-slate-500">{c.start}–{c.end} · {c.instructor_name}</div></div>
            {c.checked_in && <span className="text-emerald-600 text-sm font-bold flex items-center gap-1" data-testid={`portal-checked-${c.id}`}><CheckCircle2 className="w-4 h-4" />Feito</span>}
          </div>
          {!c.checked_in && <div className="flex gap-2">
            <Input placeholder="Código QR (opcional)" value={codes[c.id] || ""} onChange={(e) => setCodes({ ...codes, [c.id]: e.target.value.toUpperCase() })} className="font-mono uppercase" maxLength={6} data-testid={`portal-code-${c.id}`} />
            <Button onClick={() => go(c.id, codes[c.id])} className="bg-red-600 hover:bg-red-700 font-bold" data-testid={`portal-checkin-btn-${c.id}`}>Confirmar</Button>
          </div>}
        </div>))}
      <p className="text-xs text-slate-500">Dica: na academia, aponte a câmera do celular para o QR Code da recepção — o check-in é feito automaticamente.</p>
      <div className="panel p-4" data-testid="portal-checkin-history">
        <div className="font-display text-lg font-bold uppercase mb-2">Histórico de frequência</div>
        {(hist || []).slice(0, 40).map((h) => <div key={h.id} className="flex justify-between text-sm py-1.5 border-t border-slate-100"><span>{dt(h.date)} · {h.class_name}</span><span className="text-slate-500">{h.time}</span></div>)}
        {!hist?.length && <Empty />}
      </div>
    </div>
  );
}
